import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getOverview, getLive, errorMessage } from '../api/securityApi';
import SecurityNav from '../components/Security/SecurityNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import { LineChart, BarList, Donut, SevBadge, ActionBadge, sevColor } from '../components/Security/Charts';

const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

export default function SecurityOverviewPage() {
  const { sites, siteId, setSiteId } = useActiveSite();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await getOverview(siteId, days)); }
    catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, [siteId, days]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try { const l = await getLive(siteId); if (alive) setLive(l); } catch { /* keep last */ }
      if (alive) timer.current = setTimeout(tick, 10000);
    };
    tick();
    return () => { alive = false; clearTimeout(timer.current); };
  }, [siteId]);

  const s = data?.summary;
  const delta = s && s.previousTotal > 0 ? Math.round(((s.total - s.previousTotal) / s.previousTotal) * 100) : null;
  const tl = (data?.timeline || []).map((t) => ({
    label: data.granularity === 'hour' ? fmtTime(t.bucket).slice(0, 5) : t.bucket.slice(5, 10),
    threats: t.threats, blocked: t.blocked,
  }));

  return (
    <div className="shell">
      <header className="page-header">
        <div><h1>Security Center</h1><p className="muted">Invalid-traffic detection across your sites.</p></div>
      </header>
      <SecurityNav />
      <div className="sec-toolbar">
        <SiteSelector sites={sites} value={siteId} onChange={setSiteId} />
        <select className="sec-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[1, 7, 14, 30, 90].map((d) => <option key={d} value={d}>Last {d} day{d > 1 ? 's' : ''}</option>)}
        </select>
        <button className="ghost" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      {error && <div className="toast error">{error}</div>}

      {s && (
        <>
          <div className="sec-kpis">
            <div className="sec-kpi"><small>Threats detected</small><b>{s.total}</b>
              <small>{delta === null ? '—' : `${delta > 0 ? '+' : ''}${delta}% vs previous`}</small></div>
            <div className="sec-kpi"><small>IVT rate</small><b>{s.ivtRate}%</b><small>Risk: {s.riskLevel}</small></div>
            <div className="sec-kpi"><small>Blocked</small><b>{s.blocked}</b><small>{s.monitored} monitored</small></div>
            <div className="sec-kpi"><small>Open / critical</small><b>{s.open}</b><small>{s.critical} critical</small></div>
            <div className="sec-kpi"><small>Unique IPs</small><b>{s.uniqueIps}</b><small>avg risk {s.avgRisk}</small></div>
            <div className="sec-kpi"><small>Sessions analyzed</small><b>{s.sessionsAnalyzed}</b><small>{s.flaggedSessions} flagged</small></div>
            <div className="sec-kpi"><small>Active blocks</small><b>{s.activeBlocks}</b><small>{s.falsePositives} false positives</small></div>
          </div>

          <div className="sec-grid">
            <div className="sec-card" style={{ gridColumn: '1 / -1' }}>
              <h3>Threats over time</h3>
              <LineChart data={tl} series={[
                { key: 'threats', label: 'Threats', color: '#f59e0b' },
                { key: 'blocked', label: 'Blocked', color: '#dc2626' }]} />
            </div>
            <div className="sec-card"><h3>By severity</h3>
              <Donut items={(data.bySeverity || []).map((x) => ({ label: x.name, value: x.count, color: sevColor[x.name] }))} /></div>
            <div className="sec-card"><h3>By threat type</h3>
              <BarList items={(data.byType || []).map((x) => ({ label: x.name, value: x.count }))} /></div>
            <div className="sec-card"><h3>Top detection reasons</h3>
              <BarList color="#8b5cf6" items={(data.byReason || []).map((x) => ({ label: x.name, value: x.count }))} /></div>
            <div className="sec-card"><h3>Top offending IPs</h3>
              <div className="sec-scroll"><table className="sec-table">
                <thead><tr><th>IP</th><th>Events</th><th>Max risk</th><th>Last seen</th></tr></thead>
                <tbody>
                  {(data.topIps || []).map((r) => (
                    <tr key={r.ip}><td className="sec-mono">{r.ip}</td><td>{r.count}</td><td>{r.maxScore}</td>
                      <td>{new Date(r.lastSeen + (r.lastSeen.endsWith('Z') ? '' : 'Z')).toLocaleString()}</td></tr>
                  ))}
                  {(data.topIps || []).length === 0 && <tr><td colSpan="4" className="muted">No threats in this period 🎉</td></tr>}
                </tbody></table></div></div>
          </div>
        </>
      )}

      <div className="sec-card">
        <h3>Live feed {live && <small className="muted">· last 5 min: {live.last5Minutes.sessions} sessions, {live.last5Minutes.events} events, {live.last5Minutes.threats} threats</small>}</h3>
        <div className="sec-live">
          {(live?.threats || []).map((t) => (
            <div key={t.id}>
              <span className="muted">{fmtTime(t.createdAt)}</span>
              <SevBadge value={t.severity} /><ActionBadge value={t.action} />
              <span className="sec-mono">{t.ip}</span><span>score {t.riskScore}</span>
              <span className="muted">{t.reasons.join(', ')}</span>
            </div>
          ))}
          {live && live.threats.length === 0 && <div className="muted">No threats in the last hour.</div>}
        </div>
      </div>
    </div>
  );
}
